import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { listDepartments } from '@/api/departments';
import { addDepartmentAccessGroup, listDepartmentAccessGroups, removeDepartmentAccessGroup } from '@/api/departmentAccessGroups';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import {
  CoverageHint, CoveragePanel, ResizablePalette, SelectRow, type CoverageItem,
} from '@/components/common/allocationPanel';
import type { AccessGroupKind, Department, DepartmentAccessGroup, DepartmentAccessGroups } from '@/types/oscar';

const ACCESS_KEY = ['departmentAccessGroups'] as const;

type View = 'department' | 'group';

const KINDS: { kind: AccessGroupKind; label: string }[] = [
  { kind: 'OLSecurity', label: 'OnsiteLive security' },
  { kind: 'OLSubscription', label: 'OnsiteLive subscription' },
  { kind: 'BaseplanSecurity', label: 'Baseplan security' },
];

/**
 * One group of the chosen kind, as both lenses list it. OnsiteLive groups come
 * from Oscar's copy of OnsiteLive (by id); Baseplan groups are the names already
 * in use (Oscar cannot read Baseplan's list yet).
 */
interface GroupEntry {
  key: string;
  name: string;
  secondary?: string;
  /** Inactive in OnsiteLive, or gone from it. */
  flag?: string;
  /** What to send when adding it. */
  onsiteLiveGroupId?: number;
  groupName?: string;
}

// The allocation's group key — the same key the GroupEntry for it carries.
// Baseplan names compare case-insensitively, as the unique index does.
function allocKey(a: DepartmentAccessGroup): string {
  return a.Kind === 'BaseplanSecurity' ? `name:${(a.GroupName ?? '').toLowerCase()}` : `id:${a.OnsiteLiveGroupId}`;
}

function groupEntries(data: DepartmentAccessGroups, kind: AccessGroupKind): GroupEntry[] {
  const allocs = data.Allocations.filter((a) => a.Kind === kind);
  if (kind === 'BaseplanSecurity') {
    const byKey = new Map<string, GroupEntry>();
    for (const a of allocs) {
      const key = allocKey(a);
      if (!byKey.has(key)) byKey.set(key, { key, name: a.GroupName ?? '', groupName: a.GroupName ?? '' });
    }
    return [...byKey.values()].sort((x, y) => x.name.localeCompare(y.name));
  }
  const entries: GroupEntry[] = data.Groups
    .filter((g) => g.Kind === kind)
    .map((g) => ({
      key: `id:${g.GroupId}`,
      name: g.Name ?? `Group ${g.GroupId}`,
      secondary: g.Description ?? undefined,
      flag: g.Active ? undefined : 'inactive',
      onsiteLiveGroupId: g.GroupId,
    }));
  // A mapping whose group has gone from OnsiteLive still shows, so it can be removed.
  const known = new Set(entries.map((e) => e.key));
  for (const a of allocs) {
    const key = allocKey(a);
    if (a.GroupMissing && !known.has(key)) {
      known.add(key);
      entries.push({ key, name: `Group ${a.OnsiteLiveGroupId}`, flag: 'not in OnsiteLive', onsiteLiveGroupId: a.OnsiteLiveGroupId ?? undefined });
    }
  }
  return entries.sort((x, y) => x.name.localeCompare(y.name));
}

function FlagBadge({ text }: { text: string }) {
  return <span className="shrink-0 rounded-full bg-darkGrey/10 px-1.5 py-0.5 text-[10px] font-semibold text-darkGrey">{text}</span>;
}

/**
 * Onboarding — Access Groups. Which OnsiteLive security groups, OnsiteLive
 * subscription groups and Baseplan security groups a department's people get
 * (oscar.DepartmentAccessGroup; team walk-through 26/10/2026). The access twin
 * of Software Allocation: pick a kind, then two symmetric lenses —
 *   • By department (default) — pick a department → its groups of that kind.
 *   • By group — pick a group → the departments that get it.
 * The right panel splits into Allocated / Not allocated with inline allocate and
 * remove. Baseplan groups are names: a new one is typed in on the department
 * lens. Admin-only for mutation; everyone else sees the split read-only.
 */
export function AccessGroupAllocation() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<AccessGroupKind>('OLSecurity');
  const [view, setView] = useState<View>('department');
  const [removeTarget, setRemoveTarget] = useState<{ alloc: DepartmentAccessGroup; name: string } | null>(null);

  const departmentsQuery = useQuery({
    queryKey: ['departments', { includeDeleted: false }],
    queryFn: ({ signal }) => listDepartments(false, signal),
  });
  const accessQuery = useQuery({
    queryKey: ACCESS_KEY,
    queryFn: ({ signal }) => listDepartmentAccessGroups(signal),
  });

  const departments = useMemo(() => departmentsQuery.data ?? [], [departmentsQuery.data]);
  const groups = useMemo(() => (accessQuery.data ? groupEntries(accessQuery.data, kind) : []), [accessQuery.data, kind]);
  const allocations = useMemo(
    () => (accessQuery.data?.Allocations ?? []).filter((a) => a.Kind === kind),
    [accessQuery.data, kind],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ACCESS_KEY });

  const addMutation = useMutation({
    mutationFn: ({ code, group }: { code: string; group: GroupEntry }) =>
      addDepartmentAccessGroup(code, kind === 'BaseplanSecurity'
        ? { Kind: kind, GroupName: group.groupName }
        : { Kind: kind, OnsiteLiveGroupId: group.onsiteLiveGroupId }),
    onSuccess: invalidate,
  });
  // Opening the confirm clears any error left by the previous attempt.
  const openRemove = (alloc: DepartmentAccessGroup, name: string) => { removeMutation.reset(); setRemoveTarget({ alloc, name }); };
  const removeMutation = useMutation({
    mutationFn: (id: number) => removeDepartmentAccessGroup(id),
    onSuccess: () => { invalidate(); setRemoveTarget(null); },
  });

  const onAdd = (code: string, group: GroupEntry) => addMutation.mutate({ code, group });
  const changeKind = (k: AccessGroupKind) => { addMutation.reset(); setKind(k); };

  const loading = departmentsQuery.isLoading || accessQuery.isLoading;
  const errored = departmentsQuery.isError || accessQuery.isError;
  const kindLabel = KINDS.find((k) => k.kind === kind)!.label;

  return (
    <section>
      <header className="mb-5">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy">
          <KeyRound className="h-6 w-6 text-navy" aria-hidden /> Access Groups
        </h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          The OnsiteLive and Baseplan groups each department&rsquo;s people get. OnsiteLive groups are picked from OnsiteLive&rsquo;s
          own list. Baseplan groups are names: they are the checklist for whoever sets up the Baseplan user, as Oscar cannot
          write to Baseplan yet.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded border border-border bg-white p-0.5 text-sm" role="tablist" aria-label="Group kind">
            {KINDS.map((k) => (
              <button
                key={k.kind}
                type="button"
                role="tab"
                aria-selected={kind === k.kind}
                onClick={() => changeKind(k.kind)}
                className={kind === k.kind ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
              >
                {k.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded border border-border bg-white p-0.5 text-sm" role="tablist" aria-label="Allocation view">
            {([['department', 'By department'], ['group', 'By group']] as [View, string][]).map(([v, label]) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                onClick={() => setView(v)}
                className={view === v ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {addMutation.error && (
        <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">
          {addMutation.error instanceof ApiError ? addMutation.error.message : 'Could not add that group.'}
        </div>
      )}

      {loading && <p className="mt-6 text-sm text-darkGrey/60">Loading…</p>}
      {errored && <p className="mt-6 text-sm text-red">Could not load the access groups.</p>}

      {/* Keyed on the kind so a selection never carries over to a list it is not in. */}
      {!loading && !errored && view === 'department' && (
        <DepartmentView
          key={kind}
          kind={kind}
          kindLabel={kindLabel}
          departments={departments}
          groups={groups}
          allocations={allocations}
          admin={isAdmin}
          busy={addMutation.isPending}
          onAdd={onAdd}
          onRemove={openRemove}
        />
      )}

      {!loading && !errored && view === 'group' && (
        <GroupView
          key={kind}
          kindLabel={kindLabel}
          departments={departments}
          groups={groups}
          allocations={allocations}
          admin={isAdmin}
          busy={addMutation.isPending}
          onAdd={onAdd}
          onRemove={openRemove}
        />
      )}

      {removeTarget && (
        <AlertDialog
          open={removeTarget !== null}
          onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
          title={`Remove "${removeTarget.name}"?`}
          description={`This removes ${removeTarget.name} from the groups ${removeTarget.alloc.DepartmentCode}'s people get. Nobody's existing access changes. You can add it back later.`}
          confirmLabel="Remove"
          destructive
          closeOnConfirm={false}
          onConfirm={() => removeMutation.mutate(removeTarget.alloc.DepartmentAccessGroupId)}
        >
          {removeMutation.isError && (
            <p role="alert" className="text-sm text-red">
              {removeMutation.error instanceof ApiError ? removeMutation.error.message : 'Could not remove that group.'}
            </p>
          )}
        </AlertDialog>
      )}
    </section>
  );
}

interface ViewProps {
  kindLabel: string;
  departments: Department[];
  groups: GroupEntry[];
  allocations: DepartmentAccessGroup[];
  admin: boolean;
  busy: boolean;
  onAdd: (code: string, group: GroupEntry) => void;
  onRemove: (alloc: DepartmentAccessGroup, name: string) => void;
}

// ── By department: pick a department → its groups of this kind ─────────────

function DepartmentView({ kind, kindLabel, departments, groups, allocations, admin, busy, onAdd, onRemove }: ViewProps & { kind: AccessGroupKind }) {
  const [q, setQ] = useState('');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);

  const countByCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of allocations) map.set(a.DepartmentCode, (map.get(a.DepartmentCode) ?? 0) + 1);
    return map;
  }, [allocations]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? departments.filter((d) => `${d.DepartmentCode} ${d.DepartmentDescription}`.toLowerCase().includes(term)) : departments;
  }, [departments, q]);

  const selectedDept = useMemo(() => departments.find((d) => d.DepartmentCode === selectedCode) ?? null, [departments, selectedCode]);

  // One coverage row per group of this kind; targetId is the group's index.
  const items = useMemo<CoverageItem<DepartmentAccessGroup>[]>(() => {
    if (!selectedDept) return [];
    const mine = new Map(allocations.filter((a) => a.DepartmentCode === selectedDept.DepartmentCode).map((a) => [allocKey(a), a] as const));
    return groups.map((g, i) => ({
      key: g.key,
      code: g.name,
      label: g.secondary ?? '',
      mono: false,
      badge: g.flag ? <FlagBadge text={g.flag} /> : undefined,
      search: `${g.name} ${g.secondary ?? ''}`.toLowerCase(),
      alloc: mine.get(g.key) ?? null,
      targetId: i,
    }));
  }, [selectedDept, allocations, groups]);

  const nameOf = (a: DepartmentAccessGroup) => groups.find((g) => g.key === allocKey(a))?.name ?? a.GroupName ?? '';

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex-1">
        <label className="mb-3 flex flex-col text-xs font-semibold text-darkGrey">
          Search departments
          <input aria-label="Search departments" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code or description" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
        <ul className="space-y-2">
          {filtered.map((d) => (
            <SelectRow
              key={d.DepartmentCode}
              primary={d.DepartmentCode}
              secondary={d.DepartmentDescription}
              mono
              count={countByCode.get(d.DepartmentCode) ?? 0}
              selected={d.DepartmentCode === selectedCode}
              onSelect={() => setSelectedCode(d.DepartmentCode)}
            />
          ))}
          {filtered.length === 0 && <li className="text-sm text-darkGrey/60">No departments match.</li>}
        </ul>
      </div>

      <ResizablePalette>
        {selectedDept ? (
          <div className="space-y-3">
            {admin && kind === 'BaseplanSecurity' && (
              <NewBaseplanGroup
                busy={busy}
                onAdd={(name) => onAdd(selectedDept.DepartmentCode, { key: `name:${name.toLowerCase()}`, name, groupName: name })}
              />
            )}
            <CoveragePanel
              title={<>{kindLabel} — <span className="font-mono text-navy">{selectedDept.DepartmentCode}</span> <span className="text-navy">{selectedDept.DepartmentDescription}</span></>}
              searchPlaceholder="Search groups"
              items={items}
              admin={admin}
              busy={busy}
              onAllocate={(it) => onAdd(selectedDept.DepartmentCode, groups[it.targetId])}
              onRemove={(a) => onRemove(a, nameOf(a))}
            />
          </div>
        ) : (
          <CoverageHint>Select a department on the left to see which {kindLabel} groups its people get — and which they don’t.</CoverageHint>
        )}
      </ResizablePalette>
    </div>
  );
}

// ── By group: pick a group → the departments that get it ───────────────────

function GroupView({ kindLabel, departments, groups, allocations, admin, busy, onAdd, onRemove }: ViewProps) {
  const [q, setQ] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const allocByGroup = useMemo(() => {
    const map = new Map<string, DepartmentAccessGroup[]>();
    for (const a of allocations) {
      const arr = map.get(allocKey(a)) ?? [];
      arr.push(a);
      map.set(allocKey(a), arr);
    }
    return map;
  }, [allocations]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? groups.filter((g) => `${g.name} ${g.secondary ?? ''}`.toLowerCase().includes(term)) : groups;
  }, [groups, q]);

  const selected = useMemo(() => groups.find((g) => g.key === selectedKey) ?? null, [groups, selectedKey]);

  // One coverage row per department for the selected group.
  const items = useMemo<CoverageItem<DepartmentAccessGroup>[]>(() => {
    if (!selected) return [];
    const byDept = new Map((allocByGroup.get(selected.key) ?? []).map((a) => [a.DepartmentCode, a] as const));
    return departments.map((d) => ({
      key: d.DepartmentCode,
      code: d.DepartmentCode,
      label: d.DepartmentDescription,
      mono: true,
      search: `${d.DepartmentCode} ${d.DepartmentDescription}`.toLowerCase(),
      alloc: byDept.get(d.DepartmentCode) ?? null,
      targetId: 0,
    }));
  }, [selected, allocByGroup, departments]);

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="flex-1">
        <label className="mb-3 flex flex-col text-xs font-semibold text-darkGrey">
          Search groups
          <input aria-label="Search groups" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Group name" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
        <ul className="space-y-2">
          {filtered.map((g) => (
            <SelectRow
              key={g.key}
              primary={g.name}
              secondary={g.secondary}
              badge={g.flag ? <FlagBadge text={g.flag} /> : undefined}
              count={allocByGroup.get(g.key)?.length ?? 0}
              selected={g.key === selectedKey}
              onSelect={() => setSelectedKey(g.key)}
            />
          ))}
          {filtered.length === 0 && (
            <li className="text-sm text-darkGrey/60">
              {groups.length === 0 ? `No ${kindLabel} groups yet.` : 'No groups match.'}
            </li>
          )}
        </ul>
      </div>

      <ResizablePalette>
        {selected ? (
          <CoveragePanel
            title={<>Departments — <span className="text-navy">{selected.name}</span></>}
            searchPlaceholder="Search departments"
            items={items}
            admin={admin}
            busy={busy}
            onAllocate={(it) => onAdd(it.code, selected)}
            onRemove={(a) => onRemove(a, selected.name)}
          />
        ) : (
          <CoverageHint>Select a group on the left to see which departments get it — and which don’t.</CoverageHint>
        )}
      </ResizablePalette>
    </div>
  );
}

// ── A Baseplan group by name (Oscar cannot read Baseplan's list) ───────────

const baseplanSchema = z.object({
  name: z.string().trim().min(1, 'Enter the Baseplan group name.').max(150, 'At most 150 characters.'),
});
type BaseplanFormData = z.infer<typeof baseplanSchema>;

function NewBaseplanGroup({ busy, onAdd }: { busy: boolean; onAdd: (name: string) => void }) {
  const { register, handleSubmit, formState, reset } = useForm<BaseplanFormData>({
    resolver: zodResolver(baseplanSchema),
    defaultValues: { name: '' },
  });
  const submit = handleSubmit((data) => { onAdd(data.name); reset(); });

  return (
    <form onSubmit={submit} className="rounded border border-border bg-white p-3" noValidate>
      <label className="flex flex-col text-xs font-semibold text-darkGrey">
        Add a Baseplan group by name
        <div className="mt-1 flex gap-2">
          <input
            aria-label="Baseplan group name"
            {...register('name')}
            placeholder="As it is named in Baseplan"
            className="flex-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal"
          />
          <button type="submit" disabled={busy} className="rounded bg-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-navy/90 disabled:opacity-50">
            Add
          </button>
        </div>
      </label>
      {formState.errors.name && <p role="alert" className="mt-1 text-xs text-red">{formState.errors.name.message}</p>}
      <p className="mt-1 text-[11px] text-darkGrey/60">Or allocate one already in use from the list below.</p>
    </form>
  );
}
